// --- React
 import React from 'react';
 // --- Navigation
 import { navigate } from '@reach/router';
 // --- Styles
 import './PageNavigation.scss';
 // --- UI Components
 import { ExpandableMenu, ExpandableMenuSection } from '@dtci/spellbook/ExpandableMenu';
 import Icon from '@dtci/spellbook/Icon';
 import { DualPanelLayout, PrimaryPanel, SecondaryPanel } from '@dtci/spellbook/DualPanelLayout';
 import { AppFooter } from '../AppFooter';
 import { usePermissions } from "../../utils/permissions";

 /**
  * PageNavigation component responsible for rendering the layout and navigation menu.
  *
  * @param {Object} props - Component props.
  * @param {React.ReactNode} props.children - The content displayed in the primary panel.
  * @returns {JSX.Element} The rendered PageNavigation component.
  */
 export const PageNavigation = ({ children }) => {
     const { isLegalAdmin, isLegalUser, isProductCommentsAdmin } = usePermissions();

     const goTo = (path) => () => {
         navigate(path);
         localStorage.setItem('activePanel', path);
     };

     return (
         <>
             <div>
                 {/* Layout structure with primary and secondary panels */}
                 <DualPanelLayout>
                     {/* Primary panel displaying main content */}
                     <PrimaryPanel>{children}</PrimaryPanel>

                     {/* Secondary panel containing expandable menu navigation */}
                     <SecondaryPanel fill="theme" strokeWidth="thin">
                         <ExpandableMenu isExpanded>
                             <br />

                             {/* Navigation section for Products */}
                             <ExpandableMenuSection
                                 isActive={localStorage.getItem('activePanel') === '/products'}
                                 mainSection={
                                     <Icon
                                         altText="products"
                                         faPackage="fas"
                                         iconName="fa-clapperboard"
                                         iconType="fa"
                                         title="products"
                                     />
                                 }
                                 onClick={goTo('/products')}
                             >
                                 Products
                             </ExpandableMenuSection>
                             <br />

                             {/* Navigation section for Clearance Memo Titles */}
                             {(isLegalAdmin || isLegalUser || isProductCommentsAdmin) && (
                                 <ExpandableMenuSection
                                     isActive={
                                         localStorage.getItem('activePanel') === '/clearanceMemoTitles'
                                     }
                                     mainSection={
                                         <Icon
                                             altText="titles"
                                             faPackage="far"
                                             iconName="fa-film"
                                             iconType="fa"
                                             title="titles"
                                         />
                                     }
                                     onClick={goTo('/clearanceMemoTitles')}
                                 >
                                     Clearance Memo Titles
                                 </ExpandableMenuSection>
                             )}
                             <br />

                             {/* Navigation section for Shared Sections */}
                             {isLegalAdmin && (
                                 <ExpandableMenuSection
                                     isActive={localStorage.getItem('activePanel') === '/sharedSections'}
                                     mainSection={
                                         <Icon
                                             altText="shared"
                                             faPackage="fas"
                                             iconName="fa-comment"
                                             iconType="fa"
                                             title="shared"
                                         />
                                     }
                                     onClick={goTo('/sharedSections')}
                                 >
                                     Shared Sections
                                 </ExpandableMenuSection>
                             )}
                             <br />

                             {/* Navigation section for Tags */}
                             {isLegalAdmin && (
                                 <ExpandableMenuSection
                                     isActive={localStorage.getItem('activePanel') === '/tags'}
                                     mainSection={
                                         <Icon
                                             altText="tags"
                                             faPackage="fas"
                                             iconName="fa-light fa-tags"
                                             iconType="fa"
                                             title="tags"
                                         />
                                     }
                                     onClick={goTo('/tags')}
                                 >
                                     Tags
                                 </ExpandableMenuSection>
                             )}
                             <br />

                             {/* Navigation section for Contacts */}
                             {isLegalAdmin && (
                                 <ExpandableMenuSection
                                     isActive={localStorage.getItem('activePanel') === '/contacts'}
                                     mainSection={
                                         <Icon
                                             altText="contacts"
                                             faPackage="fas"
                                             iconName="fa-light fa-address-book"
                                             iconType="fa"
                                             title="contacts"
                                         />
                                     }
                                     onClick={goTo('/contacts')}
                                 >
                                     Contacts
                                 </ExpandableMenuSection>
                             )}
                         </ExpandableMenu>
                     </SecondaryPanel>
                 </DualPanelLayout>

                 {/* Footer section */}
                 <AppFooter />
             </div>
         </>
     );
 };